import { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Field, Input } from '@/components/ui';
import { LanguageSelect } from '@/components/LanguageSelect';
import { AppDownloadLink } from '@/features/app-download/AppDownloadLink';
import { useErrorMessage } from '@/hooks/useErrorMessage';
import { useAuthStore } from '@/store/auth';

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const lastLogoutReason = useAuthStore((s) => s.lastLogoutReason);
  const clearLogoutReason = useAuthStore((s) => s.clearLogoutReason);
  const toMessage = useErrorMessage();

  const [employeeNo, setEmployeeNo] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // 提示只显示一次，别让工人每次登录都看到「会话超时」
  useEffect(() => () => clearLogoutReason(), [clearLogoutReason]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const user = await login(employeeNo.trim(), password);
      if (user.mustChangePassword) {
        navigate('/change-password', { replace: true });
      } else {
        // 用哪套外壳由设备决定，不看权限（CR-040）：交给首页重定向去分
        navigate('/', { replace: true });
      }
    } catch (err) {
      setError(toMessage(err));
      setBusy(false);
    }
  }

  return (
    <div className="safe-top safe-bottom flex min-h-full flex-col items-center justify-center px-5 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-end justify-between gap-4">
          <div>
            <h1 className="text-heading font-semibold tracking-tight">{t('app.name')}</h1>
            <p className="mt-1.5 text-body leading-relaxed text-ink-3">{t('auth.loginSubtitle')}</p>
          </div>
          <LanguageSelect />
        </div>

        {/* 表单放在一张卡里（CR-081 方向稿），浅灰底上一块白 */}
        <Card className="p-6 shadow-sm">
          <form onSubmit={handleSubmit} className="flex flex-col gap-4" autoComplete="off">
            {!error && lastLogoutReason === 'clockOut' && (
              <Alert tone="success">{t('auth.clockOutLogout')}</Alert>
            )}
            {!error && lastLogoutReason === 'expired' && (
              <Alert tone="info">{t('auth.sessionEnded')}</Alert>
            )}
            {error && <Alert>{error}</Alert>}

            <Field label={t('auth.employeeNo')} htmlFor="employeeNo" required>
              <Input
                id="employeeNo"
                value={employeeNo}
                onChange={(e) => setEmployeeNo(e.target.value)}
                placeholder={t('auth.employeeNoPlaceholder')}
                // 手机是多人共用的，不能让 WebView 记住上一个人的工号和密码
                autoComplete="off"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                required
              />
            </Field>

            <Field label={t('auth.password')} htmlFor="password" required>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="off"
                required
              />
            </Field>

            <Button type="submit" block loading={busy} className="mt-2">
              {busy ? t('auth.loggingIn') : t('auth.login')}
            </Button>
          </form>
        </Card>

        {/* 服务器上挂着安卓安装包时才出现（CR-058）；App 里不显示 */}
        <div className="mt-8 empty:hidden">
          <AppDownloadLink />
        </div>
      </div>
    </div>
  );
}
